import { useEffect, useState } from "react";
import { api, formatTZS, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Upload, X } from "lucide-react";

const EMPTY = {
  name: "", description: "", price_per_night: 60000,
  capacity_adults: 2, capacity_children: 1,
  amenities: [], images: [], is_active: true,
};

export default function RoomTypesPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [amenityInput, setAmenityInput] = useState("");

  const load = async () => { const { data } = await api.get("/room-types?active_only=false"); setRows(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, description: r.description || "", price_per_night: r.price_per_night,
      capacity_adults: r.capacity_adults, capacity_children: r.capacity_children,
      amenities: r.amenities || [], images: r.images || [], is_active: r.is_active });
    setOpen(true);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("folder", "room-types");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      setForm((f) => ({ ...f, images: [...f.images, url] }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = { ...form, price_per_night: Number(form.price_per_night),
        capacity_adults: Number(form.capacity_adults), capacity_children: Number(form.capacity_children) };
      if (editing) await api.put(`/admin/room-types/${editing.id}`, payload);
      else await api.post("/admin/room-types", payload);
      toast.success(editing ? "Room type updated" : "Room type created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this room type? Requires no units attached.")) return;
    try { await api.delete(`/admin/room-types/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="room-types-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Room Types</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Define categories like Single, Double, Deluxe. Add rates & photos.</p>
        </div>
        <Button onClick={openNew} data-testid="new-room-type-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">
          <Plus className="w-4 h-4 mr-1" /> New room type
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rows.map((r) => (
          <Card key={r.id} className="overflow-hidden hover-lift" data-testid={`room-type-item-${r.id}`}>
            <div className="h-40 bg-[color:var(--sand)] relative">
              {r.images?.[0] && <img src={r.images[0]} alt={r.name} className="w-full h-full object-cover" />}
              <div className="absolute top-3 right-3 flex gap-2">
                {!r.is_active && <Badge className="bg-slate-500 text-white">Inactive</Badge>}
              </div>
            </div>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-lg text-[color:var(--deep)]">{r.name}</h3>
                  <p className="text-xs text-[color:var(--muted-text)] mt-1">{r.capacity_adults}A · {r.capacity_children}C · {r.total_units || 0} units</p>
                </div>
                <p className="font-heading text-lg text-[color:var(--brand)]">{formatTZS(r.price_per_night)}</p>
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-room-type-${r.id}`}><Edit2 className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                <Button size="sm" variant="destructive" onClick={() => remove(r.id)} data-testid={`delete-room-type-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="room-type-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit room type" : "New room type"}</DialogTitle>
            <DialogDescription>Room types (e.g. Single, Double) group individual rooms.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rt-name" placeholder="e.g. Deluxe" /></div>
              <div><Label>Price / night (TZS)</Label><Input type="number" value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })} data-testid="rt-price" /></div>
              <div><Label>Active</Label>
                <div className="mt-2 flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="rt-active-switch" /><span className="text-sm text-[color:var(--muted-text)]">Visible to guests</span></div>
              </div>
              <div><Label>Adults capacity</Label><Input type="number" min="1" value={form.capacity_adults} onChange={(e) => setForm({ ...form, capacity_adults: e.target.value })} data-testid="rt-adults" /></div>
              <div><Label>Children capacity</Label><Input type="number" min="0" value={form.capacity_children} onChange={(e) => setForm({ ...form, capacity_children: e.target.value })} data-testid="rt-children" /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="rt-description" /></div>
            </div>

            <div>
              <Label>Amenities</Label>
              <div className="flex gap-2 mt-1">
                <Input value={amenityInput} onChange={(e) => setAmenityInput(e.target.value)} placeholder="Type any amenity" data-testid="rt-amenity-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (amenityInput.trim()) { setForm({ ...form, amenities: [...form.amenities, amenityInput.trim()] }); setAmenityInput(""); } } }} />
                <Button type="button" onClick={() => { if (amenityInput.trim() && !form.amenities.includes(amenityInput.trim())) { setForm({ ...form, amenities: [...form.amenities, amenityInput.trim()] }); setAmenityInput(""); } }} variant="outline">Add</Button>
              </div>
              <div className="mt-2">
                <p className="text-xs text-[color:var(--muted-text)] mb-1.5">Quick add:</p>
                <div className="flex flex-wrap gap-1.5">
                  {["WiFi", "Hot Shower", "Breakfast", "Air Conditioning", "TV", "Fan", "Mini Bar", "Safe", "Parking", "Airport Transfer", "Gear Storage", "Safari Desk", "Room Service", "Laundry"].map((a) => {
                    const active = form.amenities.includes(a);
                    return (
                      <button key={a} type="button" onClick={() => setForm({ ...form, amenities: active ? form.amenities.filter((x) => x !== a) : [...form.amenities, a] })} data-testid={`quick-amenity-${a.replace(/\s/g, "-").toLowerCase()}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-[color:var(--brand)] text-white border-[color:var(--brand)]" : "bg-white text-[color:var(--deep)] border-[color:var(--brand)]/20 hover:bg-[color:var(--sand)]"}`}>
                        {active ? "✓ " : "+ "}{a}
                      </button>
                    );
                  })}
                </div>
              </div>
              {form.amenities.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[color:var(--brand)]/10">
                  <p className="text-xs text-[color:var(--muted-text)] w-full">Selected ({form.amenities.length}):</p>
                  {form.amenities.map((a, i) => (
                    <Badge key={i} className="gap-1 bg-[color:var(--brand)]/10 text-[color:var(--deep)] hover:bg-[color:var(--brand)]/15">{a}
                      <button onClick={() => setForm({ ...form, amenities: form.amenities.filter((_, x) => x !== i) })} data-testid={`remove-amenity-${i}`}><X className="w-3 h-3" /></button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Photos</Label>
              <p className="text-xs text-[color:var(--muted-text)] mt-0.5">The first photo is the <b>thumbnail</b> shown on the landing page & booking card. Upload multiple to build a gallery.</p>
              <div className="flex flex-wrap gap-3 mt-2">
                {form.images.map((src, i) => (
                  <div key={i} className={`relative w-28 h-28 rounded-lg overflow-hidden border-2 ${i === 0 ? "border-[color:var(--brand)]" : "border-transparent"}`} data-testid={`rt-image-${i}`}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[color:var(--brand)] text-white">Thumbnail</span>}
                    <div className="absolute top-1 right-1 flex gap-1">
                      {i > 0 && (
                        <button title="Make thumbnail" onClick={() => { const arr = [...form.images]; const [x] = arr.splice(i, 1); arr.unshift(x); setForm({ ...form, images: arr }); }} className="bg-white/90 rounded-full p-0.5 text-[10px] px-1.5" data-testid={`make-thumb-${i}`}>★</button>
                      )}
                      <button onClick={() => setForm({ ...form, images: form.images.filter((_, x) => x !== i) })} className="bg-white/90 rounded-full p-0.5" data-testid={`remove-image-${i}`}><X className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
                <label className="w-28 h-28 rounded-lg border-2 border-dashed border-[color:var(--brand)]/30 flex flex-col items-center justify-center cursor-pointer hover:bg-[color:var(--sand)] gap-1" data-testid="rt-upload">
                  {uploading ? <span className="text-xs">Uploading…</span> : <><Upload className="w-5 h-5 text-[color:var(--brand)]" /><span className="text-[10px] text-[color:var(--muted-text)]">Add photo</span></>}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-rt-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">
              {editing ? "Save changes" : "Create room type"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
